import { z } from 'zod';
import { documentSchema } from './core';
import { isValidDocument, normalizePhoneBR, onlyDigits } from './documents';

// ---------- Saque do saldo da conta de recebimento ----------

export const PIX_KEY_TYPES = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'] as const;
export type PixKeyType = (typeof PIX_KEY_TYPES)[number];
export const PIX_KEY_TYPE_LABELS: Record<PixKeyType, string> = {
  CPF: 'CPF',
  CNPJ: 'CNPJ',
  EMAIL: 'E-mail',
  PHONE: 'Celular',
  EVP: 'Chave aleatória',
};

export const BANK_ACCOUNT_TYPES = ['CHECKING', 'SAVINGS'] as const;
export type BankAccountType = (typeof BANK_ACCOUNT_TYPES)[number];

/** Normaliza a chave Pix no formato do DICT; `null` = inválida para o tipo. */
export function normalizePixKey(type: PixKeyType, key: string): string | null {
  const v = key.trim();
  if (type === 'CPF' || type === 'CNPJ') {
    const d = onlyDigits(v);
    return d.length === (type === 'CPF' ? 11 : 14) && isValidDocument(d) ? d : null;
  }
  if (type === 'EMAIL') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && v.length <= 77 ? v.toLowerCase() : null;
  if (type === 'PHONE') return normalizePhoneBR(v);
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) ? v.toLowerCase() : null;
}

const pixDestinationSchema = z
  .object({
    type: z.literal('PIX'),
    pixKeyType: z.enum(PIX_KEY_TYPES),
    pixKey: z.string().trim().min(1, 'Informe a chave Pix.').max(100),
  })
  .transform((v, ctx) => {
    const pixKey = normalizePixKey(v.pixKeyType, v.pixKey);
    if (!pixKey) {
      ctx.addIssue({ code: 'custom', path: ['pixKey'], message: `Chave Pix inválida para o tipo ${PIX_KEY_TYPE_LABELS[v.pixKeyType]}.` });
      return z.NEVER;
    }
    return { ...v, pixKey };
  });

const bankDestinationSchema = z.object({
  type: z.literal('BANK_ACCOUNT'),
  /** Código de compensação (3 dígitos), ex.: 341 Itaú. */
  bankCode: z.string().trim().regex(/^\d{3}$/, 'Código do banco com 3 dígitos.'),
  agency: z.string().trim().regex(/^\d{1,5}$/, 'Agência sem o dígito.'),
  account: z.string().trim().regex(/^\d{1,13}$/, 'Conta sem o dígito.'),
  accountDigit: z.string().trim().regex(/^[0-9xX]{1,2}$/, 'Dígito da conta.'),
  accountType: z.enum(BANK_ACCOUNT_TYPES),
  ownerName: z.string().trim().min(3).max(120),
  ownerDocument: documentSchema,
});

export const payoutDestinationSchema = z.discriminatedUnion('type', [pixDestinationSchema, bankDestinationSchema]);
export type PayoutDestination = z.output<typeof payoutDestinationSchema>;

/** MANUAL = só pelo botão; WEEKLY = toda semana no dia escolhido; MONTHLY = nos dias do mês escolhidos. */
export const PAYOUT_SCHEDULES = ['MANUAL', 'WEEKLY', 'MONTHLY'] as const;
export type PayoutSchedule = (typeof PAYOUT_SCHEDULES)[number];
export const WEEKDAY_LABELS = ['', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira'] as const;

export const payoutSettingsSchema = z
  .object({
    destination: payoutDestinationSchema,
    schedule: z.enum(PAYOUT_SCHEDULES),
    /** 1 = segunda … 5 = sexta (WEEKLY). */
    weekday: z.coerce.number().int().min(1).max(5).nullable().optional(),
    /** Dias do mês, 1 a 28 (MONTHLY). */
    monthDays: z.array(z.coerce.number().int().min(1).max(28)).max(4).nullable().optional(),
    /** Saques automáticos só saem com pelo menos este saldo (centavos). */
    minAmountCents: z.coerce.number().int().min(0).max(100_000_000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.schedule === 'WEEKLY' && !v.weekday) ctx.addIssue({ code: 'custom', path: ['weekday'], message: 'Escolha o dia da semana.' });
    if (v.schedule === 'MONTHLY' && !v.monthDays?.length) ctx.addIssue({ code: 'custom', path: ['monthDays'], message: 'Escolha pelo menos um dia do mês.' });
  })
  .transform((v) => ({
    ...v,
    weekday: v.schedule === 'WEEKLY' ? v.weekday! : null,
    monthDays: v.schedule === 'MONTHLY' ? [...new Set(v.monthDays!)].sort((a, b) => a - b) : [],
    minAmountCents: v.minAmountCents ?? null,
  }));
export type PayoutSettingsInput = z.output<typeof payoutSettingsSchema>;

/** Saque pelo botão; sem valor = todo o saldo disponível. */
export const createPayoutSchema = z.object({ amountCents: z.coerce.number().int().min(1).max(100_000_000_00).optional() });

export const PAYOUT_STATUSES = ['PENDING', 'PROCESSING', 'DONE', 'FAILED', 'CANCELED'] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];
export const PAYOUT_STATUS_LABELS: Record<PayoutStatus, string> = {
  PENDING: 'Solicitado',
  PROCESSING: 'Em processamento',
  DONE: 'Enviado',
  FAILED: 'Falhou',
  CANCELED: 'Cancelado',
};

// ---------- Documentos da conta de recebimento ----------

/** Situação de cada grupo de documentos pedido pelo meio de pagamento. */
export const ACCOUNT_DOCUMENT_STATUSES = ['NOT_SENT', 'PENDING', 'APPROVED', 'REJECTED', 'IGNORED'] as const;
export type AccountDocumentStatus = (typeof ACCOUNT_DOCUMENT_STATUSES)[number];

export const ACCOUNT_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ACCOUNT_DOCUMENT_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'] as const;

/**
 * Próxima data de saque automático a partir de `today` (dia local, AAAA-MM-DD), inclusive.
 * `null` para saque manual. Fins de semana não contam no semanal (weekday 1–5).
 */
export function nextPayoutDate(
  settings: { schedule: PayoutSchedule; weekday: number | null; monthDays: number[] },
  today: string,
): string | null {
  if (settings.schedule === 'MANUAL') return null;
  const d = new Date(`${today}T12:00:00Z`);
  for (let i = 0; i < 62; i++) {
    const day = new Date(d.getTime() + i * 86400_000);
    const due = settings.schedule === 'WEEKLY' ? day.getUTCDay() === settings.weekday : settings.monthDays.includes(day.getUTCDate());
    if (due) return day.toISOString().slice(0, 10);
  }
  return null;
}
